import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { FaCalculator, FaDollarSign, FaChartLine } from 'react-icons/fa';

const ROICalculator = () => {
  const [missedCalls, setMissedCalls] = useState(5);
  const [caseValue, setCaseValue] = useState(50000);
  const [conversionRate, setConversionRate] = useState(20);
  const [monthlyInvestment, setMonthlyInvestment] = useState(1500);
  
  const results = useMemo(() => {
    const missedCallsPerMonth = missedCalls * 4;
    const potentialCases = missedCallsPerMonth * (conversionRate / 100);
    const monthlyRevenueLeak = potentialCases * caseValue;
    const yearlyRevenueLeak = monthlyRevenueLeak * 12;
    const roiMultiplier = monthlyInvestment > 0 ? monthlyRevenueLeak / monthlyInvestment : 0;

    return {
      monthlyLeak: monthlyRevenueLeak,
      yearlyLeak: yearlyRevenueLeak,
      investment: monthlyInvestment,
      roi: roiMultiplier
    };
  }, [missedCalls, caseValue, conversionRate, monthlyInvestment]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/50" id="roi-calculator">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="flex justify-center items-center mb-6">
            <FaCalculator className="text-5xl text-emerald-400" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            The ROI Is <span className="gradient-text">Embarrassingly Simple</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Calculate exactly how much revenue you're leaving on the table
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Calculator Inputs */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-slate-800/70 backdrop-blur-sm border border-slate-700 rounded-2xl p-8"
            data-testid="roi-calculator-inputs"
          >
            <h3 className="text-2xl font-bold mb-6 text-white">Your Numbers</h3>

            <div className="space-y-6">
              {/* Missed Calls */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Missed calls per week: <span className="text-emerald-400">{missedCalls}</span>
                </label>
                <input
                  type="range"
                  min="1"
                  max="20"
                  value={missedCalls}
                  onChange={(e) => setMissedCalls(Number(e.target.value))}
                  className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                  data-testid="missed-calls-slider"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>1</span>
                  <span>20</span>
                </div>
              </div>

              {/* Case Value */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Average case value: <span className="text-emerald-400">{formatCurrency(caseValue)}</span>
                </label>
                <input
                  type="range"
                  min="10000"
                  max="200000"
                  step="10000"
                  value={caseValue}
                  onChange={(e) => setCaseValue(Number(e.target.value))}
                  className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                  data-testid="case-value-slider"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>$10K</span>
                  <span>$200K</span>
                </div>
              </div>

              {/* Conversion Rate */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Lead to case conversion rate: <span className="text-emerald-400">{conversionRate}%</span>
                </label>
                <input
                  type="range"
                  min="5"
                  max="50"
                  step="5"
                  value={conversionRate}
                  onChange={(e) => setConversionRate(Number(e.target.value))}
                  className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                  data-testid="conversion-rate-slider"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>5%</span>
                  <span>50%</span>
                </div>
              </div>

              {/* Monthly Investment */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Your monthly investment: <span className="text-emerald-400">{formatCurrency(monthlyInvestment)}/mo</span>
                </label>
                <input
                  type="range"
                  min="1500"
                  max="5000"
                  step="100"
                  value={monthlyInvestment}
                  onChange={(e) => setMonthlyInvestment(Number(e.target.value))}
                  className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                  data-testid="monthly-investment-slider"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>$1,500</span>
                  <span>$5,000</span>
                </div>
              </div>
            </div>

            <div className="mt-8 p-4 bg-slate-700/50 rounded-lg">
              <p className="text-sm text-gray-400">
                <strong className="text-white">Industry Average:</strong> PI firms miss 5-10 calls/week at $50K+ per case with 20% conversion.
              </p>
            </div>
          </motion.div>

          {/* Results */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/10 border border-emerald-500/30 rounded-2xl p-8"
            data-testid="roi-calculator-results"
          >
            <h3 className="text-2xl font-bold mb-6 text-white">Your Revenue Leak</h3>

            <div className="space-y-6">
              {/* Monthly Leak */}
              <div className="bg-slate-800/50 rounded-xl p-6">
                <div className="flex items-center mb-2">
                  <FaDollarSign className="text-emerald-400 mr-2" />
                  <span className="text-sm text-gray-400">Monthly Revenue Leak</span>
                </div>
                <div className="text-4xl font-bold gradient-text">
                  {formatCurrency(results.monthlyLeak)}
                </div>
              </div>

              {/* Yearly Leak */}
              <div className="bg-slate-800/50 rounded-xl p-6">
                <div className="flex items-center mb-2">
                  <FaChartLine className="text-emerald-400 mr-2" />
                  <span className="text-sm text-gray-400">Yearly Revenue Leak</span>
                </div>
                <div className="text-4xl font-bold gradient-text">
                  {formatCurrency(results.yearlyLeak)}
                </div>
              </div>

              {/* Investment */}
              <div className="bg-slate-800/50 rounded-xl p-6">
                <div className="flex items-center mb-2">
                  <FaCalculator className="text-emerald-400 mr-2" />
                  <span className="text-sm text-gray-400">Your Monthly Investment</span>
                </div>
                <div className="text-3xl font-bold text-white">
                  {formatCurrency(results.investment)}/mo
                </div>
              </div>

              {/* ROI */}
              <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl p-6">
                <div className="flex items-center mb-2">
                  <span className="text-sm text-slate-900 font-semibold">Return on Investment</span>
                </div>
                <div className="text-5xl font-bold text-slate-900">
                  {results.roi.toFixed(1)}x ROI
                </div>
                <p className="text-sm text-slate-800 mt-2">
                  You make back your investment <strong>{results.roi.toFixed(1)} times over</strong> every month
                </p>
              </div>
            </div>

            <div className="mt-8 p-4 bg-slate-800/50 rounded-lg border-l-4 border-emerald-500">
              <p className="text-white font-semibold mb-2">The Bottom Line:</p>
              <p className="text-gray-300 text-sm">
                If our system helps you sign just <strong className="text-emerald-400">ONE extra case per year</strong>, that single case is worth many times more than a full year of your investment in this system.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;
